import { instance } from 'api/api';
import { APIResponse } from 'tools/types';

export type QuestionState = 'Unanswered' | 'Answered' | 'Skipped' | 'Superseded' | 'Expired';
export type QuestionStatus = Exclude<QuestionState, 'Superseded' | 'Expired'>;
export type Severity = 'High' | 'Medium' | 'Low';

export interface MeasureDateDefaultOption {
    value: string; // date_object_uid
    label: string;
    description?: string;
    role?: string;
}

/** One per-measure row of a grouped `measure_date_defaults` question. */
export interface MeasureDateDefaultItem {
    itemKey: string;
    measureObjectUid?: string;
    measureName: string;
    question: string;
    options: MeasureDateDefaultOption[];
    /** Backend-marked preselection (inference ladder); optional, not projected today. */
    selectedValue?: string;
    /** Row 1511: why the date flow asked a person, the stored reason's typed key; present only on an asked measure. */
    askReason?: string;
    /** Row 1511: that reason's one catalogue line (ClarificationSentences), shown under the measure's name. */
    askReasonText?: string;
}

/**
 * One entry of the structured `answers` array the backend json-decodes from the
 * posted `answer` string for `measure_date_defaults` questions
 * (ClarificationQuestionModel::answerQuestion → MeasureDateClarificationService::answer).
 */
export interface MeasureDateDefaultAnswer {
    itemKey: string;
    dateObjectUid: string;
}

/**
 * Grouped slicer-governance question ids carry this prefix
 * (SlicerGovernanceQuestionService::QUESTION_PREFIX); the backend routes their
 * answers through the governed service, never the Learning JSON blob.
 */
export const SLICER_GOVERNANCE_QUESTION_PREFIX = 'slicer-governance__';

export const isSlicerGovernanceQuestionId = (questionId: string): boolean =>
    questionId.startsWith(SLICER_GOVERNANCE_QUESTION_PREFIX);

/** One choice of a `slicer_promotion` item (SlicerGovernanceQuestionService::options). */
export interface SlicerPromotionOption {
    /** 'use_one_at_a_time' | 'safe_to_combine' | 'not_sure' | 'same_thing' */
    value: string;
    label: string;
    /** Submitting this choice without a non-empty reason is rejected server-side. */
    requiresReason: boolean;
}

/**
 * One per-field item of the grouped `slicer_promotion` question, exactly as
 * SlicerGovernanceQuestionService::renderItem projects it. `options` is
 * authoritative: a preflight-blocked item simply lacks the activating option
 * and carries `activationBlockedNotice` instead.
 */
export interface SlicerPromotionItem {
    itemKey: string;
    kind: 'promotion' | 'activation' | 'twin' | 'growth';
    slicerUid: string;
    fieldCaption: string;
    question: string;
    samples: string[];
    memberCount: number;
    consequence: string;
    options: SlicerPromotionOption[];
    /** Per-item CAS token; echoed back on answer, stale values get the reload contract. */
    itemBasisHash: string;
    state: 'open' | 'activation_pending';
    activationBlocked: boolean;
    /** Neutral steward-facing sentence; empty when the item is not blocked. */
    activationBlockedNotice: string;
}

/** Collapsed "deferred" section rows (spec 4.8: *Not sure* answers, listed for discoverability). */
export interface SlicerPromotionDeferredItem {
    fieldCaption: string;
    deferredTime: string;
}

/**
 * One per-item result of a `slicer_promotion` answer, exactly as
 * SlicerGovernanceQuestionService::itemResult shapes it and the answer
 * endpoint returns it in `data`. The `message` strings are steward-safe,
 * built server-side.
 */
export interface SlicerPromotionItemResult {
    itemKey: string;
    status:
        | 'answered'
        | 'deferred'
        | 'activated'
        | 'activation_pending'
        | 'refused'
        | 'stale_item'
        | 'unknown_item'
        | 'reason_required'
        | 'activation_blocked'
        | 'option_not_offered';
    reasons: string[];
    message: string | null;
    questionUid: string | null;
}

/**
 * What an answer submission resolves with: the slicer governance family
 * returns its per-item results; every other family returns nothing (their
 * response `data` stays empty).
 */
export type AnswerQuestionResult = SlicerPromotionItemResult[] | void;

/**
 * What the answer hook resolves with for a slicer governance submission: the
 * per-item results (the structured control's PRIMARY outcome signal) plus
 * whether the reconciliation refetch landed. When it did not, the on-screen
 * projection is stale and absence of an item proves nothing.
 */
export interface SlicerAnswerOutcome {
    results?: SlicerPromotionItemResult[];
    reconciled: boolean;
}

/**
 * One entry of the structured `answers` array the backend json-decodes from the
 * posted `answer` string for `slicer_promotion` questions
 * (SlicerGovernanceQuestionService::answer → answerItem): the chosen option
 * value, the item CAS token, and an optional reason (mandatory server-side for
 * options flagged `requiresReason`).
 */
export interface SlicerPromotionAnswer {
    itemKey: string;
    answer: string;
    itemBasisHash: string;
    reason?: string;
}

/**
 * The grouped `slicer_promotion` question as SlicerGovernanceQuestionService
 * projects it. Kept as a parallel shape rather than a widening of
 * `ElementClarifyingQuestion.items` so the measure-date contract stays
 * untouched; the `type` field discriminates at runtime and
 * `asSlicerPromotionQuestion` (slicer-promotion helpers) performs the cast.
 */
export interface SlicerPromotionQuestion {
    questionId: string;
    question: string;
    options: string[];
    rationale: string;
    status: QuestionStatus;
    answer?: string | null;
    type: 'slicer_promotion';
    groupKey: string;
    items: SlicerPromotionItem[];
    deferredItems: SlicerPromotionDeferredItem[];
}

export interface ElementClarifyingQuestion {
    questionId: string;
    question: string;
    options: string[];
    rationale: string;
    status: QuestionStatus;
    answer?: string | null;
    /** 'measure_date_defaults' or 'slicer_promotion' for grouped questions; absent otherwise. */
    type?: string | null;
    groupKey?: string | null;
    items?: MeasureDateDefaultItem[];
    /** Server-recorded per-item answers of a partially answered grouped question. */
    partialAnswers?: MeasureDateDefaultAnswer[];
    /** `slicer_promotion` only: the collapsed deferred section rows. */
    deferredItems?: SlicerPromotionDeferredItem[];
}

export interface RawClarifyingQuestion extends Omit<ElementClarifyingQuestion, 'status'> {
    status: QuestionState;
}

export interface ElementInconsistency {
    measureId: string;
    measureName: string;
    severity: Severity;
    modelCount: number;
    modelType: string;
    modelUid?: string | null;
    reportCount: number;
    resolveUrl: string;
}

export type MissingAccess = 'privilege' | 'report_edit';

export interface ElementIssuesContext {
    isEditAccess: boolean;
    // Manage Knowledge Graph privilege (MI-29733 §5.2): gates the inconsistency Resolve / Review all affordances
    isKgManager?: boolean;
    // What keeps the user from resolving (Publishing_Board.md §3.4.2); empty when nothing is missing
    missingAccess?: MissingAccess[];
    clarifyingQuestions: ElementClarifyingQuestion[];
    inconsistencies: ElementInconsistency[];
    overlappingMeasures: string[];
}

export interface RawIssuesContext extends Omit<ElementIssuesContext, 'clarifyingQuestions'> {
    clarifyingQuestions: RawClarifyingQuestion[];
}

export const elementIssuesAPI = {
    async getContext(elementId: number): Promise<RawIssuesContext> {
        const response = await instance.get<APIResponse<RawIssuesContext>>(
            `data/extreport/element/${elementId}/issues`,
        );
        return response.data.data;
    },

    async answerQuestion(elementId: number, questionId: string, answer: string): Promise<AnswerQuestionResult> {
        const response = await instance.post<APIResponse<unknown>>(
            `data/extreport/element/${elementId}/clarifying-question/${questionId}/answer`,
            { answer },
        );
        // A per-item results array is the slicer governance family's answer
        // shape alone; other families answer with an empty array or an object
        // (the element endpoint returns `{auto_enabled: […]}`).
        const data = response.data.data;
        return Array.isArray(data) && data.length > 0 ? (data as SlicerPromotionItemResult[]) : undefined;
    },

    async skipQuestion(elementId: number, questionId: string): Promise<void> {
        await instance.post(`data/extreport/element/${elementId}/clarifying-question/${questionId}/skip`);
    },

    async undoQuestion(elementId: number, questionId: string): Promise<void> {
        await instance.post(`data/extreport/element/${elementId}/clarifying-question/${questionId}/undo`);
    },
};
